import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./PublicConsentForm.css"; // Reuse consent form styles

interface WithdrawalFormData {
  candidateFirstName: string;
  consentGivenAt: string | null;
  warningText: string;
  confirmationText: string;
}

const PublicWithdrawalForm: React.FC = () => {
  const { t } = useTranslation("public");
  const { token } = useParams<{ token: string }>();
  const [formData, setFormData] = useState<WithdrawalFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    const fetchWithdrawalData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(
          `/api/recruiting/public/consent/${token}/withdraw`,
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          setFormData(data.data);
        } else {
          setError(
            data.error || data.message || t("withdrawalForm.invalidLink"),
          );
        }
      } catch (err) {
        setError(t("withdrawalForm.loadError"));
        console.error("Error fetching withdrawal data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchWithdrawalData();
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasConfirmed) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `/api/recruiting/public/consent/${token}/withdraw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason }),
        },
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
      } else {
        setError(
          data.error || data.message || t("withdrawalForm.withdrawError"),
        );
      }
    } catch (err) {
      console.error("Error submitting withdrawal:", err);
      setError(t("withdrawalForm.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="consent-form-container">
        <div className="consent-loading">
          <div className="spinner"></div>
          <p>{t("withdrawalForm.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="consent-form-container">
        <div className="consent-error">
          <div className="error-icon">⚠️</div>
          <h2>{t("withdrawalForm.unableToLoad")}</h2>
          <p>{error}</p>
          <p className="error-help">{t("withdrawalForm.errorHelp")}</p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="consent-form-container">
        <div className="consent-success withdrawal-success">
          <div className="success-icon withdrawal-icon">✓</div>
          <h2>{t("withdrawalForm.consentWithdrawn")}</h2>
          <p>{t("withdrawalForm.withdrawnSuccess")}</p>
          <div className="withdrawal-info">
            <p>{t("withdrawalForm.gdprAnonymizeMessage")}</p>
            <p className="info-note">{t("withdrawalForm.noLongerContacted")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consent-form-container">
      <div className="consent-form-wrapper withdrawal-wrapper">
        <div className="consent-header withdrawal-header">
          <div className="company-logo">
            <img src="/hirefocuz-logo.png" alt="HireFocuZ" />
          </div>
          <h1>{t("withdrawalForm.title")}</h1>
          {formData?.candidateFirstName && (
            <p className="greeting">
              {t("withdrawalForm.greeting", {
                name: formData.candidateFirstName,
              })}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="consent-form">
          <div className="withdrawal-warning-section">
            <div className="warning-icon">⚠️</div>
            <h2>{t("withdrawalForm.importantInfo")}</h2>
            <p className="warning-text">{formData?.warningText}</p>
          </div>

          {formData?.consentGivenAt && (
            <div className="consent-info-section">
              <p>
                <strong>{t("withdrawalForm.consentGivenOn")}:</strong>{" "}
                {new Date(formData.consentGivenAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          )}

          <div className="reason-section">
            <label htmlFor="withdrawal-reason">
              <strong>{t("withdrawalForm.reasonLabel")}:</strong>
            </label>
            <textarea
              id="withdrawal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("withdrawalForm.reasonPlaceholder")}
              rows={3}
              className="withdrawal-textarea"
              disabled={isSubmitting}
            />
          </div>

          <div className="consent-checkbox-section">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={hasConfirmed}
                onChange={(e) => setHasConfirmed(e.target.checked)}
                disabled={isSubmitting}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                {formData?.confirmationText ||
                  t("withdrawalForm.defaultConfirmationText")}
              </span>
            </label>
          </div>

          <div className="consent-submit-section">
            <button
              type="submit"
              className="btn-consent-submit btn-withdrawal"
              disabled={!hasConfirmed || isSubmitting}
            >
              {isSubmitting
                ? t("withdrawalForm.withdrawing")
                : t("withdrawalForm.withdrawConsent")}
            </button>
          </div>

          <div className="consent-footer">
            <p className="footer-note">{t("withdrawalForm.footerNote")}</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublicWithdrawalForm;
